/**
 * ConfirmModal — Accessible confirmation dialog, replacing window.confirm().
 *
 * Props:
 *   isOpen          boolean
 *   title           string
 *   message         string
 *   confirmLabel    string                   (default: 'Confirm')
 *   confirmVariant  'danger' | 'primary'     (default: 'danger')
 *   onConfirm       fn()
 *   onCancel        fn()
 */
import { useEffect, useRef } from 'react'

export default function ConfirmModal({
  isOpen,
  title,
  message,
  confirmLabel = 'Confirm',
  confirmVariant = 'danger',
  onConfirm,
  onCancel,
}) {
  const cancelRef  = useRef(null)
  const confirmRef = useRef(null)

  useEffect(() => {
    if (!isOpen) return

    // Focus the cancel button by default (safe default for destructive actions)
    cancelRef.current?.focus()

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onCancel()

      // Trap focus inside modal
      if (e.key === 'Tab') {
        const focusable = [cancelRef.current, confirmRef.current].filter(Boolean)
        if (!focusable.length) return
        const first = focusable[0]
        const last  = focusable[focusable.length - 1]
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault()
            last.focus()
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault()
            first.focus()
          }
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onCancel])

  if (!isOpen) return null

  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-modal-title"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel() }}
    >
      <div className="modal-box confirm-modal-box">
        <div className="modal-header">
          <h2 className="modal-title" id="confirm-modal-title">{title}</h2>
        </div>
        {message && <p className="confirm-modal-message">{message}</p>}
        <div className="modal-footer">
          <button
            ref={cancelRef}
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            ref={confirmRef}
            type="button"
            className={`btn btn-${confirmVariant}`}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
